import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Trash2, RefreshCw, ArchiveRestore, Filter } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ASSETS, BRAND, ENQUIRY_TYPES } from "@/lib/brand";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const STATUSES = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "responded", label: "Responded" },
  { value: "archived", label: "Archived" },
];

const TYPE_LABELS = Object.fromEntries(ENQUIRY_TYPES.map((t) => [t.value, t.label]));

function StatusBadge({ status }) {
  const tone = {
    new: "bg-pe-maroon text-white",
    read: "bg-pe-ink/10 text-pe-ink",
    responded: "bg-emerald-700 text-white",
    archived: "bg-pe-ink/60 text-white",
  }[status] || "bg-pe-ink/10 text-pe-ink";
  return <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase ${tone}`}>{status}</span>;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("pe_admin_user") || "null"); } catch { return null; }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.get(`/admin/enquiries${filter !== "all" ? `?status_filter=${filter}` : ""}`),
        api.get("/admin/stats"),
      ]);
      setItems(list.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("pe_admin_token");
        localStorage.removeItem("pe_admin_user");
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load enquiries.", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const logout = () => {
    localStorage.removeItem("pe_admin_token");
    localStorage.removeItem("pe_admin_user");
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}.`);
      await load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      toast.error("Update failed.", { description: formatApiError(e) });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry deleted.");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error("Delete failed.", { description: formatApiError(e) });
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-pe-cream flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-pe-ink text-pe-cream flex-col">
        <div className="p-6 border-b border-white/10">
          <img src={ASSETS.logo_light} alt={BRAND.name} className="h-14 w-auto object-contain" />
          <h1 className="font-serif text-xl mt-4">Admin</h1>
          <p className="text-pe-cream/60 text-xs mt-1">{user?.email}</p>
        </div>
        <nav className="flex-1 p-4 space-y-1 text-sm">
          <div className="px-3 py-2 bg-white/5 text-pe-orange">Enquiries</div>
        </nav>
        <div className="p-4">
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full inline-flex items-center justify-center gap-2 border border-white/15 text-pe-cream px-4 py-3 text-[11px] tracking-[0.15em] uppercase hover:bg-white/5 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-pe-border px-5 md:px-10 py-5 flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-pe-ink">Enquiries</h2>
            <p className="text-pe-ink/60 text-sm">{stats.total} total · {stats.by_status?.new || 0} new</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="p-2 text-pe-ink/70 hover:text-pe-maroon" aria-label="Refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} className="md:hidden p-2 text-pe-ink/70 hover:text-pe-maroon" aria-label="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* Stat strip */}
        <div className="px-5 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-pe-ink/10">
          {[
            { v: stats.total, l: "Total enquiries" },
            { v: stats.by_status?.new || 0, l: "New" },
            { v: stats.by_status?.responded || 0, l: "Responded" },
            { v: stats.by_type?.franchise || 0, l: "Franchise" },
          ].map((s) => (
            <div key={s.l} className="bg-white p-5">
              <div className="font-serif text-3xl text-pe-maroon leading-none">{s.v}</div>
              <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-pe-ink/60">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="px-5 md:px-10 py-4 flex items-center gap-3">
          <Filter size={14} className="text-pe-ink/60" />
          <span className="text-xs uppercase tracking-[0.15em] text-pe-ink/60">Status</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-status-filter" className="w-44 rounded-none border-pe-ink/15">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="px-5 md:px-10 pb-12">
          <div className="bg-white border border-pe-border overflow-x-auto">
            <Table data-testid="admin-enquiries-table">
              <TableHeader>
                <TableRow className="border-pe-border">
                  <TableHead className="text-[10px] tracking-[0.18em] uppercase">Date</TableHead>
                  <TableHead className="text-[10px] tracking-[0.18em] uppercase">Name</TableHead>
                  <TableHead className="text-[10px] tracking-[0.18em] uppercase">Type</TableHead>
                  <TableHead className="text-[10px] tracking-[0.18em] uppercase">City</TableHead>
                  <TableHead className="text-[10px] tracking-[0.18em] uppercase">Status</TableHead>
                  <TableHead className="text-[10px] tracking-[0.18em] uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-pe-ink/50">Loading enquiries...</TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16 text-pe-ink/50">No enquiries yet.</TableCell>
                  </TableRow>
                ) : items.map((e) => (
                  <TableRow
                    key={e.id}
                    data-testid={`admin-enquiry-row-${e.id}`}
                    className="cursor-pointer hover:bg-pe-cream/60 border-pe-border"
                    onClick={() => setSelected(e)}
                  >
                    <TableCell className="text-xs text-pe-ink/70 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-pe-ink">{e.full_name}</div>
                      <div className="text-xs text-pe-ink/60">{e.email}</div>
                    </TableCell>
                    <TableCell className="text-xs">{TYPE_LABELS[e.enquiry_type] || e.enquiry_type}</TableCell>
                    <TableCell className="text-xs text-pe-ink/70">{e.city_state || "—"}</TableCell>
                    <TableCell><StatusBadge status={e.status} /></TableCell>
                    <TableCell className="text-right">
                      <a
                        onClick={(ev) => ev.stopPropagation()}
                        href={`mailto:${e.email}?subject=Re: Your enquiry to Pizza Excellente`}
                        className="inline-flex items-center gap-1 text-pe-maroon text-xs hover:underline"
                        data-testid={`admin-reply-${e.id}`}
                      >
                        <Mail size={12} /> Reply
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="admin-enquiry-dialog" className="max-w-2xl rounded-none">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{selected.full_name}</DialogTitle>
                <DialogDescription className="text-pe-ink/60">
                  {TYPE_LABELS[selected.enquiry_type]} · {new Date(selected.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="eyebrow text-pe-ink/50">Email</div>
                    <a href={`mailto:${selected.email}`} className="text-pe-maroon hover:underline">{selected.email}</a>
                  </div>
                  <div>
                    <div className="eyebrow text-pe-ink/50">Phone</div>
                    <div>{selected.phone || "—"}</div>
                  </div>
                  <div>
                    <div className="eyebrow text-pe-ink/50">City</div>
                    <div>{selected.city_state || "—"}</div>
                  </div>
                  <div>
                    <div className="eyebrow text-pe-ink/50">Source</div>
                    <div>{selected.source_page || "—"}</div>
                  </div>
                </div>
                <div>
                  <div className="eyebrow text-pe-ink/50 mb-2">Message</div>
                  <p className="whitespace-pre-wrap leading-relaxed text-pe-ink/80 bg-pe-cream p-4">{selected.message}</p>
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-pe-border">
                  {["new", "read", "responded", "archived"].map((s) => (
                    <button
                      key={s}
                      data-testid={`admin-set-status-${s}`}
                      onClick={() => updateStatus(selected.id, s)}
                      disabled={selected.status === s}
                      className="px-3 py-2 text-[10px] tracking-[0.15em] uppercase border border-pe-ink/15 hover:bg-pe-ink hover:text-pe-cream disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-pe-ink transition-colors"
                    >
                      <ArchiveRestore size={12} className="inline mr-1" />Mark {s}
                    </button>
                  ))}
                  <button
                    data-testid="admin-delete-enquiry"
                    onClick={() => remove(selected.id)}
                    className="px-3 py-2 text-[10px] tracking-[0.15em] uppercase text-pe-maroon hover:bg-pe-maroon hover:text-white border border-pe-maroon transition-colors ml-auto"
                  >
                    <Trash2 size={12} className="inline mr-1" /> Delete
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
